import type { PrV2CommentResponse, PrV2ReviewResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';

import type {
  PrV2LocalCommentModelView,
  PrV2LocalCommentReadModelComment,
  PrV2LocalScopeState,
} from './types';

import { EMPTY_VIEW, ensureScope } from './types';

import {
  buildPrV2CommentFromDiscussionTimelineItem,
  buildPrV2ReviewFromDiscussionTimelineItem,
} from '@/lib/pr-v2/timeline-comment-projection';

export const buildTimelineComment = buildPrV2CommentFromDiscussionTimelineItem;
export const buildTimelineReview = buildPrV2ReviewFromDiscussionTimelineItem;

function getEffectiveThread(thread: PrV2ReviewThread, optimisticResolvedOverride: boolean | null) {
  if (optimisticResolvedOverride === null || thread.isResolved === optimisticResolvedOverride) {
    return thread;
  }

  return {
    ...thread,
    isResolved: optimisticResolvedOverride,
  };
}

function buildReviewLookupMaps(scope: PrV2LocalScopeState): {
  reviewThreads: PrV2ReviewThread[];
  reviewByLookupId: Map<string, PrV2ReviewResponse>;
  reviewThreadByCommentNodeId: Map<string, PrV2ReviewThread>;
} {
  const reviewThreads: PrV2ReviewThread[] = [];
  const reviewByLookupId = new Map<string, PrV2ReviewResponse>();
  for (const clientId of scope.reviewOrder) {
    const entity = scope.reviewsByClientId[clientId];
    if (!entity) {
      continue;
    }

    for (const lookupId of entity.lookupIds) {
      reviewByLookupId.set(lookupId, entity.review);
    }
  }

  const reviewThreadByCommentNodeId = new Map<string, PrV2ReviewThread>();
  for (const clientId of scope.threadOrder) {
    const entity = scope.threadsByClientId[clientId];
    if (!entity) {
      continue;
    }

    const effectiveThread = getEffectiveThread(entity.thread, entity.optimisticResolvedOverride);
    reviewThreads.push(effectiveThread);

    for (const node of effectiveThread.comments.nodes) {
      reviewThreadByCommentNodeId.set(node.id, effectiveThread);
    }
  }

  return { reviewThreads, reviewByLookupId, reviewThreadByCommentNodeId };
}

export function buildView(scope: PrV2LocalScopeState | undefined): PrV2LocalCommentModelView {
  const resolvedScope = ensureScope(scope);
  if (resolvedScope.commentOrder.length === 0) {
    return {
      ...EMPTY_VIEW,
      ...buildReviewLookupMaps(resolvedScope),
    };
  }

  const aliasLookupToClientId = new Map<string, string>();
  const readModelEntries: PrV2LocalCommentReadModelComment[] = [];

  for (const clientId of resolvedScope.commentOrder) {
    const entity = resolvedScope.commentsByClientId[clientId];
    if (!entity) {
      continue;
    }

    for (const lookupId of entity.lookupIds) {
      aliasLookupToClientId.set(lookupId, clientId);
    }
  }

  for (const clientId of resolvedScope.commentOrder) {
    const entity = resolvedScope.commentsByClientId[clientId];
    if (!entity) {
      continue;
    }

    const parentDisplayId = entity.comment.inReplyToId
      ? (aliasLookupToClientId.get(entity.comment.inReplyToId) ?? null)
      : null;

    readModelEntries.push({
      clientId,
      comment: entity.comment,
      lifecycle: entity.lifecycle,
      displayId: clientId,
      parentDisplayId,
      threadId: '',
    });
  }

  const entryByClientId = new Map(readModelEntries.map((entry) => [entry.clientId, entry]));
  for (const entry of readModelEntries) {
    let currentParentId = entry.parentDisplayId;
    let threadRootId = entry.clientId;
    const visited = new Set<string>();

    // Thread ids are derived from stable local client ids, not transport ids. That keeps
    // reply draft namespaces and thread-card identity stable across optimistic promotion.
    while (currentParentId && !visited.has(currentParentId)) {
      visited.add(currentParentId);
      threadRootId = currentParentId;
      currentParentId = entryByClientId.get(currentParentId)?.parentDisplayId ?? null;
    }

    entry.threadId = `comment:${threadRootId}`;
  }

  const commentByLookupId = new Map<string, PrV2CommentResponse>();
  const readModelByLookupId = new Map<string, PrV2LocalCommentReadModelComment>();
  const commentByGithubId = new Map<string, PrV2LocalCommentReadModelComment>();

  for (const entry of readModelEntries) {
    const entity = resolvedScope.commentsByClientId[entry.clientId];
    if (!entity) {
      continue;
    }

    commentByGithubId.set(entry.comment.githubCommentId, entry);
    for (const lookupId of entity.lookupIds) {
      commentByLookupId.set(lookupId, entry.comment);
      readModelByLookupId.set(lookupId, entry);
    }
  }

  const { reviewThreads, reviewByLookupId, reviewThreadByCommentNodeId } =
    buildReviewLookupMaps(resolvedScope);

  return {
    comments: readModelEntries.map((entry) => entry.comment),
    reviewThreads,
    commentReadModel: readModelEntries,
    commentByLookupId,
    readModelByLookupId,
    commentByGithubId,
    reviewByLookupId,
    reviewThreadByCommentNodeId,
  };
}
